import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { OrganizationRole, TeamMember, TeamRow } from "@gioia365/contracts";
import { canActOn, isSuperAdminRole } from "@gioia365/domain/access";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { rolesQuery, teamQuery, useTab, useUpdateMembership } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { assignableRoles, canCopyRole, useAccess, type Access } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { describeRole, summarizeRoles } from "./roles-in-words";
import { memberLook } from "./team";
import { teamErrorMessage } from "./team-dialogs";
import "./roles.css";

// Ruoli e permessi, scheda «Utenti» (piano M1b Task 8, mock-up `permessi.html`): il ruolo di ogni persona, che
// si cambia subito da un select (con la tastiera si conferma con Invio), e sotto «Chi può fare cosa, in parole».
// Chi modifica ha `manage_roles`;
// cambiare il ruolo di una persona è una modifica del team (`PATCH /api/memberships/:id`), quindi chiede anche
// `manage_team`. Le regole di livello e di poteri (`@gioia365/domain/access`, questione 38 del 30 settembre
// 2026) decidono soltanto cosa si mostra: il server controlla comunque ogni richiesta e un suo rifiuto si spiega.

const isPerson = (row: TeamRow): row is TeamMember => row.kind === "membership" && row.status !== "archived";

export function UsersTab() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const titleId = useId();
  const team = useQuery(teamQuery(tab));
  const roles = useQuery(rolesQuery(tab));
  const organization = access.context.organization.name;
  const canManageRoles = access.can("manage_roles");
  const canChangeRole = canManageRoles && access.can("manage_team");
  const rows = team.data;
  const allRoles = roles.data?.roles;
  // Per ogni ruolo la frase che lo distingue dagli altri, per la colonna «Cosa fa».
  const summaries = useMemo(() => (allRoles ? summarizeRoles(t, allRoles) : null), [allRoles, t]);

  let content: ReactNode;
  if (team.isError || roles.isError) {
    content = (
      <div className="g-roles__problem">
        <Reason tone="warning" role="alert">{messageForError(t, team.error ?? roles.error)}</Reason>
        <Button
          onClick={() => {
            void team.refetch();
            void roles.refetch();
          }}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  } else if (!rows || !allRoles || !summaries) {
    content = <p className="g-roles__muted">{t("common.loading")}</p>;
  } else {
    // Chi non fa più parte dell'azienda (archiviato) e chi ha soltanto un invito non hanno un ruolo da cambiare.
    const people = rows.filter(isPerson);
    const assignable = assignableRoles(access, allRoles);
    content = (
      <>
        <section className="g-roles__section" aria-labelledby={titleId}>
          <div className="g-roles__head">
            <h2 id={titleId}>{t("roles.users.title")}</h2>
            <p className="g-roles__muted">{t("roles.users.help")}</p>
          </div>
          {canManageRoles && !canChangeRole ? <Reason>{t("roles.needsTeam")}</Reason> : null}
          <DataTable caption={t("roles.users.caption", { organization })} captionHidden>
            <thead>
              <tr>
                <th scope="col">{t("roles.users.columns.person")}</th>
                <th scope="col">{t("roles.users.columns.role")}</th>
                <th scope="col">{t("roles.users.columns.does")}</th>
                <th scope="col">
                  <span className="g-visually-hidden">{t("roles.users.columns.action")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <UserRow
                  key={person.membershipId}
                  tab={tab}
                  member={person}
                  access={access}
                  roles={allRoles}
                  summaries={summaries}
                  assignable={assignable}
                  organization={organization}
                  canChangeRole={canChangeRole}
                  canCustomize={canManageRoles}
                />
              ))}
            </tbody>
          </DataTable>
        </section>
        <RulesInWords roles={allRoles} organization={organization} />
      </>
    );
  }

  return <>{content}</>;
}

type RowProps = {
  tab: TabContext;
  member: TeamMember;
  access: Access;
  roles: readonly OrganizationRole[];
  /** La frase che distingue ogni ruolo dagli altri (`summarizeRoles`), per chi non ha una descrizione. */
  summaries: ReadonlyMap<string, string>;
  /** I ruoli che chi guarda può dare (`assignableRoles`): livello e poteri, questione 38. */
  assignable: readonly OrganizationRole[];
  organization: string;
  canChangeRole: boolean;
  canCustomize: boolean;
};

// I tasti che muovono la scelta di un select chiuso senza aprirlo: nei browser che scattano `change` a ogni passo
// (Chrome e Firefox su Windows) una freccia salverebbe il ruolo sbagliato prima di arrivare a quello giusto.
// Vale anche la ricerca a battuta (una lettera).
const MOVING_KEYS: ReadonlySet<string> = new Set(["ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"]);
const movesChoice = (event: KeyboardEvent<HTMLSelectElement>) =>
  MOVING_KEYS.has(event.key) || (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey);

function UserRow({ tab, member, access, roles, summaries, assignable, organization, canChangeRole, canCustomize }: RowProps) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const update = useUpdateMembership(tab);
  // Il ruolo mostrato dal select quando non è quello salvato: una scelta in attesa del server, oppure (con la
  // tastiera) non ancora confermata. Torna a quello salvato se il server rifiuta o si preme Esc.
  const [chosen, setChosen] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  // Il `change` in corso viene da un tasto (il tasto è giù); si azzera al rilascio e a un clic.
  const fromKeyboard = useRef(false);
  // Una richiesta in volo, letta anche da un evento che arriva prima del nuovo disegno (Invio e poi uscita dal campo).
  const saving = useRef(false);
  // Il select è spento mentre il server risponde e il focus andrebbe perso: si rimette dov'era.
  const restoreFocus = useRef(false);

  const saved = member.role.id;
  const draft = chosen !== null && chosen !== saved && !update.isPending ? chosen : null;
  const isSelf = member.membershipId === access.context.membershipId;
  // Il titolare è protetto per tutti, anche per la piattaforma; per sé stessi c'è «Il tuo account»; e si agisce
  // soltanto sotto il proprio livello.
  const actionable = canActOn(access.actor, { membershipId: member.membershipId, roleLevel: member.role.level, isProtectedOwner: member.isProtectedOwner }).ok;
  const full = roles.find((candidate) => candidate.id === saved);
  const current = assignable.find((candidate) => candidate.id === saved);
  const editable = canChangeRole && actionable && current !== undefined;
  // Il ruolo di sistema (il Super admin), o un ruolo del suo livello, non si cambia; se ce l'ha chi non è il
  // titolare, si dice questo e non che ha poteri che chi guarda non ha. Poi un ruolo con poteri che chi guarda non
  // ha non si cambia da qui (come nel Team). Il motivo resta scritto.
  const superAdminRole = canChangeRole && actionable && full !== undefined && isSuperAdminRole(full);
  const beyondPowers = canChangeRole && actionable && !superAdminRole && current === undefined;
  // «Ruolo su misura» copia il ruolo: mai uno di livello Super admin, e il server la rifiuta se i suoi poteri
  // superano quelli di chi copia (`canCopyRole`).
  const customizable = canCustomize && actionable && full !== undefined && canCopyRole(access, full);
  const does = full ? (full.description ?? summaries.get(full.id)) : undefined;

  // Una promessa per ogni richiesta e non i callback di `mutate`, che valgono soltanto per l'ultima chiamata.
  function save(roleId: string) {
    if (saving.current || roleId === saved) return;
    saving.current = true;
    restoreFocus.current = document.activeElement === selectRef.current;
    setChosen(roleId);
    void update.mutateAsync({ membershipId: member.membershipId, body: { roleId } }).then(
      ({ membership }) => {
        // La risposta prende subito il posto della riga; la rilettura del team la conferma.
        queryClient.setQueryData<TeamRow[]>(teamQuery(tab).queryKey, (rows) =>
          rows?.map((row) => (row.kind === "membership" && row.membershipId === membership.membershipId ? membership : row)),
        );
        void queryClient.invalidateQueries({ queryKey: rolesQuery(tab).queryKey });
        saving.current = false;
        setChosen(null);
        toast.show(t("roles.users.changed", { name: member.displayName, role: membership.role.name }), "success");
      },
      (error: unknown) => {
        saving.current = false;
        setChosen(null);
        toast.show(teamErrorMessage(t, error, { organization }), "danger");
      },
    );
  }

  useEffect(() => {
    if (!update.isPending && restoreFocus.current) {
      restoreFocus.current = false;
      selectRef.current?.focus();
    }
  }, [update.isPending]);

  // Con il mouse o il tocco il cambio vale subito («vale subito»); con la tastiera resta in attesa.
  function handleChange(roleId: string) {
    if (saving.current) return;
    if (fromKeyboard.current) setChosen(roleId === saved ? null : roleId);
    else save(roleId);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLSelectElement>) {
    if (movesChoice(event)) fromKeyboard.current = true;
    else if (event.key === "Enter" && draft !== null) {
      event.preventDefault();
      save(draft);
    } else if (event.key === "Escape" && chosen !== null && !saving.current) {
      event.preventDefault();
      setChosen(null);
    }
  }

  let role: ReactNode;
  if (editable) {
    role = (
      <Select
        ref={selectRef}
        id={`roles-user-${member.membershipId}`}
        label={t("roles.users.roleOf", { name: member.displayName })}
        hideLabel
        value={chosen ?? saved}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={() => {
          fromKeyboard.current = false;
        }}
        onPointerDown={() => {
          fromKeyboard.current = false;
        }}
        onBlur={() => {
          fromKeyboard.current = false;
          if (draft !== null) save(draft);
        }}
        options={assignable.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
        hint={draft !== null ? t("roles.users.unsaved") : undefined}
        disabled={update.isPending}
        aria-busy={update.isPending ? "true" : undefined}
      />
    );
  } else {
    role = (
      <span className="g-roles__role">
        <span>{member.role.name}</span>
        {member.isProtectedOwner ? <Chip icon="lock">{t("team.protected")}</Chip> : null}
      </span>
    );
  }

  // Chi non è attivo lo dice accanto al nome, con la stessa icona e lo stesso tono del Team: il ruolo si cambia
  // lo stesso, ma la persona non entra.
  const look = member.status === "active" ? null : memberLook(member);

  return (
    <tr>
      <th scope="row">
        <span className="g-roles__who">
          <strong>{member.displayName}</strong>
          {member.isProtectedOwner ? <Chip>{t("team.owner")}</Chip> : null}
          {isSelf ? <Chip tone="accent">{t("team.you")}</Chip> : null}
          {look ? <StatusPill tone={look.tone} icon={look.icon}>{t(look.label)}</StatusPill> : null}
        </span>
        <span className="g-table__sub">{member.email}</span>
      </th>
      <td>
        {role}
        {superAdminRole ? (
          <span className="g-table__sub g-roles__reason">{t(full?.isSystem === true ? "roles.users.systemRole" : "team.fields.roleSuperAdminLevel")}</span>
        ) : null}
        {beyondPowers ? <span className="g-table__sub g-roles__reason">{t("team.fields.roleReadOnly")}</span> : null}
      </td>
      <td className="g-roles__does">{does}</td>
      <td>
        {customizable ? (
          <Button
            variant="ghost"
            aria-label={t("roles.users.customFor", { name: member.displayName })}
            onClick={() => void navigate({ to: "/impostazioni/ruoli-e-permessi", search: { scheda: "ruoli", suMisura: member.membershipId } })}
          >
            {t("roles.users.custom")}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

type WordsProps = { roles: readonly OrganizationRole[]; organization: string };

/** «Chi può fare cosa, in parole»: una card per ruolo, dal livello più alto (a pari livello, l'ordine del server). */
function RulesInWords({ roles, organization }: WordsProps) {
  const t = useT();
  const titleId = useId();
  const sorted = [...roles].sort((first, second) => second.level - first.level);
  return (
    <section className="g-roles__section" aria-labelledby={titleId}>
      <div className="g-roles__head">
        <h2 id={titleId}>{t("roles.words.title")}</h2>
        <p className="g-roles__muted">{t("roles.words.intro", { organization })}</p>
      </div>
      <div className="g-roles__cards">
        {sorted.map((role) => {
          const headingId = `${titleId}-${role.id}`;
          return (
            <article key={role.id} className="g-card g-roles__card" aria-labelledby={headingId}>
              <div className="g-roles__card-head">
                <h3 id={headingId}>{role.name}</h3>
                <span className="g-roles__level">{t("roles.words.level", { level: role.level })}</span>
              </div>
              {role.description ? <p className="g-roles__muted g-roles__description">{role.description}</p> : null}
              <ul className="g-roles__sentences">
                {describeRole(t, role).map((sentence) => (
                  <li key={sentence}>{sentence}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </section>
  );
}
